import { useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TuneIcon from "@mui/icons-material/Tune";
import APP_CONSTANTS from "../../../utils/config";
import { InboxOutlined } from "@mui/icons-material";
import {
  getLocalStorageData,
  hasLocalStorageKey,
  setLocalStorageData,
} from "./reviewColumnUtils";

import { useTheme } from "@mui/styles";

function KeyValue({ label, value, theme, idx }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        py: 1,
        px: 1,
        background:
          idx % 2 !== 0
            ? theme.palette.brain.drawer.item_background
            : theme.palette.brain.table.row.background,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          width: "48%",
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: theme.palette.brain.table.header.text,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
        >
          {label}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "12px",
          color: theme.palette.brain.table.row.text,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function SKUDetailsDrawer({ open, data, onClose }) {
  const [expanded, setExpanded] = useState(new Set());
  const [visibleKeys, setVisibleKeys] = useState(new Set());
  const [colAnchor, setColAnchor] = useState(null);
  const skus = data?.sku_details?.rows || [];
  const columns = data?.sku_details?.columns || [];
  const displayedColumns = useMemo(() => {
    return columns.filter((c) => visibleKeys.has(c.key));
  }, [visibleKeys]);
  const theme = useTheme();

  const handleVisibleColumns = () => {
    if (columns?.length === 0) return;

    const visibleColumnKeys = getLocalStorageData(
      APP_CONSTANTS.VISIBLE_SKU_COLUMN
    );
    const localVisibleColumnKeys = columns.map((item, _) => item.key);

    const hasKey = hasLocalStorageKey(APP_CONSTANTS.VISIBLE_SKU_COLUMN);
    const isSame = visibleColumnKeys.every((item) =>
      localVisibleColumnKeys.includes(item)
    );

    if (hasKey && isSame) {
      setVisibleKeys(new Set(visibleColumnKeys));
    } else {
      updateVisibleKey(new Set(localVisibleColumnKeys));
    }
  };

  const toggle = (code) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });

  const updateVisibleKey = (updatedColumnKeySet) => {
    setLocalStorageData(APP_CONSTANTS.VISIBLE_SKU_COLUMN, [
      ...updatedColumnKeySet,
    ]);
    setVisibleKeys(updatedColumnKeySet);
  };

  const toggleKey = (key) => {
    const next = new Set(visibleKeys);
    next.has(key) ? next.delete(key) : next.add(key);
    updateVisibleKey(next);
  };

  useEffect(() => {
    if (!open) return;
    // setExpanded(skus?.length >= 1 ? new Set([skus[0].sku]) : null);
    handleVisibleColumns();
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${theme.palette.brain.drawer.item_border}`,
          background: theme.palette.brain.table.header.background,
        }}
      >
        <Inventory2OutlinedIcon
          sx={{ fontSize: 20, color: theme.palette.brain.table.header.text }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9rem",
                color: theme.palette.brain.table.header.text,
              }}
            >
              SKUs
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.85rem",
                color: theme.palette.brain.yellow.text,
              }}
            >
              {data?.upc}
            </Typography>
            <Chip
              label={`${skus.length}`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                fontWeight: 700,
                bgcolor: theme.palette.brain.yellow.background,
                color: theme.palette.brain.yellow.text,
              }}
            />
          </Box>
          {data?.description && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.brain.table.row.text }}
              noWrap
              display="block"
            >
              {data?.description}
            </Typography>
          )}
        </Box>
        <Tooltip title="Customize fields" arrow>
          <IconButton
            size="small"
            onClick={(e) => setColAnchor(e.currentTarget)}
          >
            <TuneIcon
              sx={{
                fontSize: 18,
                color: colAnchor
                  ? theme.palette.secondary.main2
                  : theme.palette.brain.table.header.text,
              }}
            />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon
            sx={{ fontSize: 18, color: theme.palette.brain.table.header.text }}
          />
        </IconButton>
      </Box>

      {/* Showing count */}
      {skus?.length !== 0 && (
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderBottom: `1px solid ${theme.palette.brain.drawer.item_border}`,
            backgroundColor: theme.palette.brain.drawer.item_hover_background,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.brain.table.row.text }}
          >
            Showing {displayedColumns.length} of {columns.length} fields
          </Typography>
        </Box>
      )}

      {/* SKU Data */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
          backgroundColor: theme.palette.brain.drawer.background,
        }}
      >
        {skus?.length === 0 ? (
          <Box
            sx={{
              borderRadius: 1.5,
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              bgcolor: theme.palette.brain.table.background1,
              height: "100%",
              minHeight: "300px",
            }}
          >
            <InboxOutlined
              sx={{
                fontSize: 40,
                color: theme.palette.secondary.main2,
                mb: 1,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.brain.table.header.text,
              }}
            >
              No Records found
            </Typography>
          </Box>
        ) : (
          skus.map((s) => {
            const isOpen = expanded.has(s.sku);
            return (
              <Box
                key={s.sku}
                sx={{
                  mb: 1.25,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  border: `1px solid ${theme.palette.brain.drawer.item_border}`,
                  bgcolor: theme.palette.brain.drawer.item_background,
                }}
              >
                <ButtonBase
                  onClick={() => toggle(s.sku)}
                  sx={{
                    width: "100%",
                    px: 1.5,
                    py: 1.1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    textAlign: "left",
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: theme.palette.secondary.main2,
                      }}
                    >
                      {s.sku}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.brain.table.header.text }}
                      noWrap
                      display="block"
                    >
                      {s.description_sap}
                    </Typography>
                  </Box>
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.brain.grey.text,
                      transition: "transform .2s",
                      transform: isOpen ? "rotate(180deg)" : "none",
                    }}
                  />
                </ButtonBase>

                <Collapse in={isOpen} unmountOnExit>
                  <Divider />
                  <Box>
                    {displayedColumns.length === 0 ? (
                      <Box
                        sx={{
                          py: 1,
                          px: 1,
                          border: "1px dashed",
                          borderColor: "divider",
                          borderRadius: 2,
                          bgcolor: theme.palette.brain.table.row.background,

                          margin: "8px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.6,
                          }}
                        >
                          Select columns using the{" "}
                          <TuneIcon
                            sx={{
                              fontSize: 18,
                              color: theme.palette.brain.table.header.text,
                              verticalAlign: "text-bottom",
                              mx: 0.25,
                            }}
                          />{" "}
                          icon to view their values.
                        </Typography>
                      </Box>
                    ) : (
                      displayedColumns.map((c, index) => (
                        <KeyValue
                          key={c.key}
                          label={c.label}
                          value={s[c.key] || "—"}
                          theme={theme}
                          idx={index}
                        />
                      ))
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>

      {/* Customize SKU */}
      <Popover
        open={!!colAnchor}
        anchorEl={colAnchor}
        onClose={() => setColAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 460,
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.brain.table.tableBackground,
          },
        }}
      >
        <Box
          sx={{
            p: 1.25,
            borderBottom: `1px solid ${theme.palette.brain.drawer.item_border}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.8rem",
              color: theme.palette.brain.table.header.text,
              mb: 0.75,
            }}
          >
            Customize fields
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.75 }}>
            <Button
              size="small"
              sx={{
                fontSize: "0.68rem",
                textTransform: "none",
                color: theme.palette.secondary.main2,
              }}
              onClick={() => {
                updateVisibleKey(new Set(columns.map((c) => c.key)));
              }}
            >
              Select all
            </Button>
            <Button
              size="small"
              sx={{
                fontSize: "0.68rem",
                textTransform: "none",
                color: theme.palette.secondary.main2,
              }}
              onClick={() => {
                updateVisibleKey(new Set());
              }}
            >
              Clear all
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 0.5, overflow: "auto", margin: "12px 0" }}>
          {columns.map((c, index) => {
            return (
              <Box key={c.key} sx={{ mb: 0.5 }}>
                <FormControlLabel
                  key={c.key}
                  sx={{ display: "flex", ml: 0.5, my: -0.35 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={visibleKeys.has(c.key)}
                      onChange={() => toggleKey(c.key)}
                      sx={{
                        color: theme.palette.secondary.main2,
                        padding: "8px",
                        "&.Mui-checked": {
                          color: theme.palette.secondary.main2,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      {c.label}
                    </Typography>
                  }
                />
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Drawer>
  );
}
